import { useCallback, useEffect, useRef } from 'react';

/**
 * Перетаскивание горизонтальной ленты мышью.
 *
 * ## Зачем
 *
 * На тачскрине лента листается пальцем, а мышью — только колесом с Shift или
 * полосой прокрутки, о которых половина посетителей не знает. Перетаскивание
 * — то, чего от такой ленты ждут (#1330).
 *
 * ## Почему Pointer Events, а не mouse
 *
 * Один набор обработчиков на мышь, перо и тач. На тач-устройствах при этом
 * НЕ вмешиваемся: там прокрутка уже работает нативно и лучше — с инерцией и
 * snap. Ловим только `pointerType === 'mouse'`.
 *
 * ## Почему захват указателя
 *
 * `setPointerCapture` доводит события до элемента, даже если курсор ушёл за
 * его пределы. Без него лента застревала на полпути, стоило увести мышь за
 * край окна, и оставалась в состоянии «тащат», пока не кликнешь снова.
 *
 * ## Клик после перетаскивания
 *
 * Карточка в ленте — ссылка. Без защиты каждое перетаскивание заканчивалось
 * переходом на карточку, за которую тянули. Поэтому клик гасится, если
 * указатель сдвинулся больше чем на `CLICK_SLOP` — короткое дрожание руки
 * при обычном клике в этот порог укладывается.
 *
 * @returns {React.RefObject} ref для элемента с горизонтальной прокруткой.
 */
const CLICK_SLOP = 5;

export function useDragScroll() {
  const ref = useRef(null);
  const state = useRef({
    active: false,
    captured: false,
    startX: 0,
    startLeft: 0,
    moved: 0,
  });

  const onPointerDown = useCallback((event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const node = ref.current;
    if (!node) return;

    // Захват — НЕ здесь, а когда мышь реально сдвинулась (#1456). Захваченный
    // при нажатии указатель уводит `click` на саму ленту: браузер шлёт его
    // элементу, который держит захват, и обычный клик по карточке в ленте не
    // открывал машину вовсе — ни фото, ни кнопка «Voir les détails».
    state.current = {
      active: true,
      captured: false,
      startX: event.clientX,
      startLeft: node.scrollLeft,
      moved: 0,
    };
  }, []);

  const onPointerMove = useCallback((event) => {
    const node = ref.current;
    if (!node || !state.current.active) return;
    const dx = event.clientX - state.current.startX;
    state.current.moved = Math.max(state.current.moved, Math.abs(dx));
    // Дрожание руки в пределах порога — ещё клик, ленту не трогаем.
    if (!state.current.captured) {
      if (state.current.moved <= CLICK_SLOP) return;
      state.current.captured = true;
      node.setPointerCapture?.(event.pointerId);
      node.classList.add('is-dragging');
    }
    node.scrollLeft = state.current.startLeft - dx;
  }, []);

  const endDrag = useCallback((event) => {
    const node = ref.current;
    if (!node || !state.current.active) return;
    state.current.active = false;
    if (!state.current.captured) return;
    state.current.captured = false;
    node.releasePointerCapture?.(event.pointerId);
    node.classList.remove('is-dragging');
  }, []);

  const onClickCapture = useCallback((event) => {
    if (state.current.moved > CLICK_SLOP) {
      event.preventDefault();
      event.stopPropagation();
    }
    state.current.moved = 0;
  }, []);

  // Сброс на размонтировании: если элемент исчез во время перетаскивания,
  // класс `is-dragging` остался бы на нём вместе с курсором «схвачено».
  useEffect(() => {
    const node = ref.current;
    return () => node?.classList.remove('is-dragging');
  }, []);

  return {
    ref,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  };
}

export default useDragScroll;
